import { Rectangle, Skeleton, showToast } from '@lokalise/louis'
import { RagAnalyticsEventName } from '@lokalise/types'
import clsx from 'clsx'
import { useEffect, useState } from 'react'
import { useAiProfilePermissionUtilities } from '../../hooks/useAiProfilePermissionUtilities.ts'
import { useAvailableAiProfiles } from '../../hooks/useAvailableAiProfiles.ts'
// TODO TRAP-1082
// import { useFetchTeamEvaluationData } from '../../hooks/useFetchTeamEvaluationData.ts'
import { useInitialiseProfiles } from '../../hooks/useInitialiseProfiles'
import { useRagAnalytics } from '../../providers/analytics'
import { AssignmentModal as TMAssignmentModal } from '../AssignmentModal/AssignmentModal'
import { ProfileCard } from '../Cards/ProfileCard/ProfileCard'
import { ProfileCardStatic } from '../Cards/ProfileCardStatic/ProfileCardStatic'
import { ProfileCardTM } from '../Cards/ProfileCardTM/ProfileCardTM'
import { LandingPageBanner } from '../LandingPageBanner/LandingPageBanner.tsx'
import styles from './LandingPage.module.css'

type LandingPageProps = {
  enableTMEvaluations?: boolean
  enableEvaluations?: boolean
}

export const LandingPage = ({ enableEvaluations, enableTMEvaluations }: LandingPageProps) => {
  const track = useRagAnalytics()
  const [isTMAssignmentModalOpen, setIsTMAssignmentModalOpen] = useState(false)

  const { profiles, pending: isLoadingAiConfigs } = useAvailableAiProfiles()
  const { isLoadingPermissions, permissions, canEdit } = useAiProfilePermissionUtilities()
  // TODO TRAP-1082: Temporarily skipped as we're unsure on how to handle limits from a product perspective
  // const { data: evaluations, isPending: isLoadingEvaluationsData } = useFetchTeamEvaluationData({
  //   enabled: enableEvaluations,
  // })

  useEffect(() => {
    track(RagAnalyticsEventName.AI_PROFILE_PAGE_OPENED)
  }, [track])

  // First time user visits page, create a TM config entry
  const { isPending: isCreatingAiConfig } = useInitialiseProfiles()

  // NB: Know that there will only be two entries for now, but this won't hold once the user has the option of creating their own configs
  if (isLoadingAiConfigs || isCreatingAiConfig || isLoadingPermissions) {
    return (
      <div className="flex-col gap-5">
        <div className={styles.cardsContainer}>
          <div className={styles.contents}>
            <Skeleton
              className="flex flex-wrap gap-5 justify-center"
              description="Loading AI configurations"
              animated
            >
              <Rectangle width="45%" height={184} />
              <Rectangle width="45%" height={184} />
            </Skeleton>
          </div>
        </div>
      </div>
    )
  }

  const { staticProfile, predefinedProfile } = permissions
  const isOnlyStaticProfileVisible = staticProfile.canView && !predefinedProfile.canView

  const tmProfile = profiles?.predefinedProfile
  const tmInitialDate = tmProfile?.config.rag.created_after
    ? new Date(tmProfile.config.rag.created_after)
    : undefined

  return (
    <div className={styles.container}>
      <div className={styles.cardsContainer}>
        <div className={clsx(styles.contents, isOnlyStaticProfileVisible && styles.singleCard)}>
          {profiles?.staticProfile ? (
            <ProfileCardStatic
              profile={profiles.staticProfile}
              showEdit={canEdit(profiles?.staticProfile)}
            />
          ) : null}
          {tmProfile ? (
            <ProfileCardTM
              status={tmProfile.status}
              title={tmProfile.name}
              profile={tmProfile}
              hasEditPermissions={canEdit(tmProfile)}
              hasTMEvaluationEnabled={enableTMEvaluations}
              onOpenAssignmentModal={() => setIsTMAssignmentModalOpen(true)}
            />
          ) : null}
          {profiles?.customProfiles?.map((profile) => (
            <ProfileCard
              key={profile.id}
              profile={profile}
              enableEvaluations={enableEvaluations}
              // TODO TRAP-1082
              // evaluationsLimit={evaluations?.usageLimit}
              // evaluationsRenewalDate={evaluations?.renewalDate}
              // isAtEvaluationsLimit={!!evaluations && evaluations?.used === evaluations?.usageLimit}
              // isLoadingEvaluations={isLoadingEvaluationsData}
              showEdit={canEdit(profile)}
            />
          ))}
        </div>
        <LandingPageBanner
          permissions={permissions}
          isTMProfileActivated={!!tmProfile?.projects.length}
          isCustomProfileActivated={
            !!profiles?.customProfiles.some((profile) => !!profile.projects.length)
          }
          onClickTmCta={() => setIsTMAssignmentModalOpen(true)}
          enableEvaluations={enableEvaluations}
        />
      </div>
      {isTMAssignmentModalOpen && tmProfile && (
        <TMAssignmentModal
          onActivateSuccess={() => {
            showToast({ type: 'success', title: `${tmProfile.name} has been updated` })
            setIsTMAssignmentModalOpen(false)
          }}
          onClose={() => setIsTMAssignmentModalOpen(false)}
          isEdit={tmProfile.status === 'active'}
          initialDate={tmInitialDate}
          initialProjects={tmProfile.projects}
          initialSourceLang={tmProfile.config.rag?.source_languages?.[0]}
          initialTargetLangs={tmProfile.config.rag.target_languages}
          profile={tmProfile}
        />
      )}
    </div>
  )
}
